import Swup from 'swup';
import SwupHeadPlugin from '@swup/head-plugin';
import SwupPreloadPlugin from '@swup/preload-plugin';
import SwupScrollPlugin from '@swup/scroll-plugin';
import SwupA11yPlugin from '@swup/a11y-plugin';
import SwupJsPlugin from '@swup/js-plugin';
import { initHistory, trackDirection } from './history.js';
import { resolveTransition } from './transitions.js';

const SCROLL_KEYS = new Set([
    'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
    'PageUp', 'PageDown', 'Home', 'End', ' ', 'Spacebar',
]);

/**
 * Block user-initiated scrolling (wheel, touchmove, scroll-keys) until the
 * returned disposer is called. Programmatic scrolls via `window.scrollTo` /
 * `scrollIntoView` are NOT blocked — Swup's scroll-plugin and the flip
 * transitions' `positionCarouselForCard` both rely on those.
 *
 * Rationale: during a Swup transition, flip-expand/flip-collapse pin cloned
 * elements at `position: fixed` viewport coordinates and tween them to
 * destination rects that were sampled at the start of the transition. If the
 * user scrolls mid-transition, the destination rects move under the clones
 * and the morph lands in the wrong place.
 */
function lockUserScroll() {
    const blockEvent = (e) => e.preventDefault();
    const blockKey = (e) => {
        if (SCROLL_KEYS.has(e.key)) e.preventDefault();
    };
    const opts = { passive: false, capture: true };
    document.addEventListener('wheel', blockEvent, opts);
    document.addEventListener('touchmove', blockEvent, opts);
    document.addEventListener('keydown', blockKey, opts);
    return () => {
        document.removeEventListener('wheel', blockEvent, opts);
        document.removeEventListener('touchmove', blockEvent, opts);
        document.removeEventListener('keydown', blockKey, opts);
    };
}

/**
 * Wait for whatever a transition module's out/in returned. Accepts a Promise,
 * a GSAP timeline, or any thenable; resolves once the work is done.
 */
function waitFor(result) {
    if (!result) return Promise.resolve();
    if (typeof result.then === 'function') return result;
    if (typeof result.eventCallback === 'function') {
        // GSAP timeline — register an onComplete to bridge to a promise.
        return new Promise((resolve) => {
            result.eventCallback('onComplete', resolve);
        });
    }
    return Promise.resolve();
}

/**
 * Create and configure the Swup router instance.
 *
 * `native: false` so Swup uses our JsPlugin transitions (not the View
 * Transitions API). Each visit, the JsPlugin's catch-all rule resolves the
 * transition module via `resolveTransition()` and invokes its `out`/`in`
 * functions, which return either a Promise or a GSAP timeline.
 *
 * @returns {Swup} Configured Swup instance
 */
export function createRouter() {
    initHistory();

    const swup = new Swup({
        containers: ['#swup', '#page-title'],
        native: false,
        cache: true,
        animateHistoryBrowsing: true,
        // Exclude Cloudflare-rewritten mailto links (`/cdn-cgi/l/email-protection#...`)
        // from Swup routing & preloading. Cloudflare's Scrape Shield rewrites
        // `mailto:` hrefs at the edge; their client-side script de-obfuscates
        // on click. Swup's preload plugin was otherwise GET'ing these URLs and
        // logging 404s.
        linkSelector: 'a[href]:not([href*="/cdn-cgi/"])',
        plugins: [
            new SwupHeadPlugin(),
            new SwupPreloadPlugin(),
            new SwupScrollPlugin({
                // Jump instantly to top (or the cached position on back/forward)
                // rather than smooth-scrolling. Smooth-scrolling overlaps the in
                // transition — the hero reveal can finish before the viewport
                // has reached the top of the new page. Same-page anchor clicks
                // keep their smooth scroll.
                animateScroll: {
                    betweenPages: false,
                    samePageWithHash: true,
                    samePage: true,
                },
            }),
            new SwupA11yPlugin(),
            new SwupJsPlugin([
                {
                    from: '(.*)',
                    to: '(.*)',
                    out: async (done, ...args) => {
                        // JsPlugin's argument shape varies by version; the visit
                        // object lives on `swup.visit` reliably.
                        const visit = swup.visit;
                        const mod = await resolveTransition(visit);
                        await waitFor(mod.out?.(visit));
                        done();
                    },
                    in: async (done, ...args) => {
                        const visit = swup.visit;
                        const mod = await resolveTransition(visit);
                        await waitFor(mod.in?.(visit));
                        done();
                    },
                },
            ]),
        ],
    });

    swup.hooks.on('visit:start', (visit) => {
        resolveTransition(visit);
        trackDirection(visit);
        document.body.dataset.transition = visit.animation.name;
    });

    swup.hooks.on('visit:start', () => {
        if (window.Alpine?.store('nav')) {
            window.Alpine.store('nav').mobileOpen = false;
        }
    });

    // Lock user scroll while the transition is running. Stored on the visit
    // so aborts can release it via `visit:abort` even if `visit:end` never
    // fires (e.g. the user triggers another navigation mid-transition).
    swup.hooks.on('visit:start', (visit) => {
        visit.releaseScrollLock = lockUserScroll();
    });

    const releaseLock = (visit) => {
        visit?.releaseScrollLock?.();
        if (visit) visit.releaseScrollLock = null;
    };
    swup.hooks.on('visit:end', releaseLock);
    swup.hooks.on('visit:abort', releaseLock);

    return swup;
}
